import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth.store';
import { registerApi, fetchMe } from '@/lib/api';
import { ApiClientError } from '@goodchat/api-client';

function getInviteFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('invite') ?? '';
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setOrgs = useAuthStore((s) => s.setOrgs);

  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [inviteToken, setInviteToken] = useState(getInviteFromUrl);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await registerApi({
        email,
        password,
        fullName,
        displayName,
        homeCity: homeCity || undefined,
        inviteToken,
      });
      setAuth(res.user, res.accessToken, res.refreshToken);

      const me = await fetchMe();
      setOrgs(me.orgs);

      navigate({ to: '/' });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Join your team on GoodChat
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
              {error}
            </div>
          ) : null}

          <Input
            id="inviteToken"
            label="Invite Token"
            required
            value={inviteToken}
            onChange={(e) => setInviteToken(e.target.value)}
            placeholder="Paste your invite token"
          />

          <Input
            id="fullName"
            label="Full Name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Smith"
          />

          <Input
            id="displayName"
            label="Display Name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane"
          />

          <Input
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@company.com"
          />

          <Input
            id="password"
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
          />

          <Input
            id="homeCity"
            label="Home City (optional)"
            value={homeCity}
            onChange={(e) => setHomeCity(e.target.value)}
            placeholder="San Francisco"
          />

          <Button type="submit" loading={loading} className="w-full">
            Create account
          </Button>
        </form>

        <div className="space-y-2 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
              Sign in
            </Link>
          </p>
          <p>
            <Link to="/privacy" className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
              Privacy
            </Link>
            <span className="mx-2">&middot;</span>
            <Link to="/terms" className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
              Terms
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
